/**
 * Post-login redirect helpers.
 *
 * CR-5887: deep-links in the Daily Quality Summary email (and any other
 * authenticated link a logged-out user follows) used to be discarded — the
 * user was bounced to /login and then sent to the role default (/dashboard)
 * instead of the page they asked for. ProtectedRoute now stashes the
 * attempted location in router state as `from`; Login / MfaVerify read it
 * back through {@link getSafeRedirectTarget} and navigate there.
 */

/** The minimal shape of a captured react-router location we care about. */
interface CapturedLocation {
  pathname?: string;
  search?: string;
  hash?: string;
}

/**
 * Build a safe in-app redirect path from a captured `from` location.
 *
 * Returns `null` unless the path is a same-origin internal route, so a
 * crafted history-state object can't drive an open redirect, a
 * protocol-relative jump, or a loop back to the auth pages.
 *
 * @param state - The `location.state` object set by ProtectedRoute (expected
 *   to carry a `from` location). Anything else yields `null`.
 * @returns A `pathname?search#hash` string safe to pass to `navigate()`, or
 *   `null` when no safe internal target is present.
 */
export function getSafeRedirectTarget(state: unknown): string | null {
  const from = (state as { from?: CapturedLocation } | null)?.from;
  const pathname = from?.pathname;
  // Must be an absolute, single-slash internal path. Reject protocol-relative
  // ("//evil.com") and anything not rooted at "/".
  if (!pathname || !pathname.startsWith('/') || pathname.startsWith('//')) {
    return null;
  }
  // Never redirect back onto the auth pages — that loops or strands the user.
  const authPaths = ['/login', '/mfa-verify', '/mfa-setup', '/forgot-password', '/reset-password'];
  if (authPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return null;
  }
  return `${pathname}${from?.search ?? ''}${from?.hash ?? ''}`;
}
